// Replaces PersonalInfoModal — full page with back navigation.
// Pre-fills from KYC data if profile fields are empty.
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation }    from "react-router-dom";
import { updateProfile, getUser }      from "../../api/apiServices";
import { useAuth }                      from "../../context/useAuth";
import PageLoader                       from "../../components/PageLoader";

export default function PersonalInfoPage() {
  const navigate     = useNavigate();
  const location     = useLocation();
  const { updateUser } = useAuth();
  const fileInputRef = useRef(null);

  // Which field to auto-focus — set by navigating /profile/personal?focus=phone
  const focusField = new URLSearchParams(location.search).get("focus");

  const [user,        setUser]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);
  const [error,       setError]       = useState(null);
  const [isEditing,   setIsEditing]   = useState(!!focusField); // auto-enter edit mode when deep-linked
  const [previewUrl,  setPreviewUrl]  = useState(null);
  const [draft,       setDraft]       = useState({});

  useEffect(() => {
    getUser()
      .then(data => {
        if (data?.status) {
          const u = data.user;
          setUser(u);
          const initial = {
            firstName:    u.firstName    || "",
            lastName:     u.lastName     || "",
            userName:     u.userName     || "",
            email:        u.email        || "",
            phone:        u.phone        || "",
            dateOfBirth:  u.dateOfBirth  || u.kycInfo?.dateOfBirth  || "",
            gender:       u.gender       || u.kycInfo?.gender       || "",
            address:      u.location?.address || u.kycInfo?.address || "",
            state:        u.location?.state   || u.kycInfo?.state   || "",
            country:      u.location?.country || "Nigeria",
            profileImage: u.profileImage || "",
          };
          setDraft(initial);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Auto-scroll to the focused field
  useEffect(() => {
    if (focusField && !loading) {
      setTimeout(() => {
        document.getElementById(`field-${focusField}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        document.getElementById(`input-${focusField}`)?.focus();
      }, 300);
    }
  }, [focusField, loading]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("firstName",   draft.firstName);
      formData.append("lastName",    draft.lastName);
      formData.append("phone",       draft.phone);
      formData.append("userName",    draft.userName || "");
      formData.append("dateOfBirth", draft.dateOfBirth || "");
      formData.append("gender",      draft.gender || "");
      formData.append("address",     draft.address || "");
      formData.append("state",       draft.state || "");
      if (fileInputRef.current?.files[0]) {
        formData.append("profileImage", fileInputRef.current.files[0]);
      }

      const res = await updateProfile(formData);
      if (res.status) {
        setPreviewUrl(null);
        setIsEditing(false);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);

        updateUser({
          firstName:    draft.firstName,
          lastName:     draft.lastName,
          phone:        draft.phone,
          userName:     draft.userName,
          profileImage: res.user.profileImage || draft.profileImage,
          location: { address: draft.address, state: draft.state, country: draft.country },
        });
        setUser(prev => ({ ...prev, ...draft, profileImage: res.user.profileImage || draft.profileImage }));
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageLoader size="md" message="Loading profile…" />;

  const fields = [
    { key: "firstName",   label: "First Name",    icon: "ri-user-3-line",       type: "text"  },
    { key: "lastName",    label: "Last Name",      icon: "ri-user-3-line",       type: "text"  },
    { key: "userName",    label: "Username",       icon: "ri-at-line",           type: "text"  },
    { key: "email",       label: "Email",          icon: "ri-mail-line",         type: "email", locked: true },
    { key: "phone",       label: "Phone Number",   icon: "ri-phone-line",        type: "tel"   },
    { key: "dateOfBirth", label: "Date of Birth",  icon: "ri-cake-line",         type: "date"  },
    { key: "gender",      label: "Gender",         icon: "ri-user-smile-line",   type: "select",
      options: ["", "male", "female", "prefer_not_to_say"] },
    { key: "address",     label: "Home Address",   icon: "ri-map-pin-2-line",    type: "text"  },
    { key: "state",       label: "State",          icon: "ri-building-2-line",   type: "text"  },
    { key: "country",     label: "Country",        icon: "ri-earth-line",        type: "text",  locked: true },
  ];

  const avatarSrc = previewUrl || draft.profileImage || null;

  return (
    <div className="min-h-screen bg-bg">
      {/* Header */}
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border flex items-center gap-3">
        <button onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-xl bg-surface-hover hover:bg-surface-hover transition-colors flex-shrink-0">
          <i className="ri-arrow-left-line text-secondary text-lg" />
        </button>
        <div className="flex-1">
          <h1 className="text-primary font-bold text-lg font-display">
            Personal Information
          </h1>
          <p className="text-muted text-xs">Manage your profile details</p>
        </div>
        {!isEditing ? (
          <button onClick={() => setIsEditing(true)}
            className="flex items-center gap-1.5 bg-brand-light border border-brand-border text-brand-text text-sm font-semibold px-3 py-2 rounded-xl hover:opacity-90 transition-colors">
            <i className="ri-edit-line text-sm" />Edit
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={() => { setIsEditing(false); setDraft({ ...user }); setPreviewUrl(null); }}
              className="text-secondary text-sm font-medium px-3 py-2 rounded-xl hover:bg-surface-hover transition-colors">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              className="bg-brand text-on-brand text-sm font-semibold px-4 py-2 rounded-xl disabled:opacity-50 flex items-center gap-1.5">
              {saving && <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>

      <div className="px-5 pt-5 pb-12 space-y-5">

        {/* Success / Error banners */}
        {saved && (
          <div className="flex items-center gap-2 bg-success-light border border-success-border rounded-2xl px-4 py-3">
            <i className="ri-checkbox-circle-fill text-success text-base" />
            <p className="text-success-text text-sm font-medium">Profile updated successfully!</p>
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 bg-danger-light border border-danger-border rounded-2xl px-4 py-3">
            <i className="ri-error-warning-line text-danger text-base" />
            <p className="text-danger-text text-sm font-medium">{error}</p>
          </div>
        )}

        {/* Avatar */}
        <div className="flex flex-col items-center">
          <div className="relative">
            <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-surface shadow-elevated bg-brand-light">
              {avatarSrc
                ? <img src={avatarSrc} alt="Profile" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center">
                    <span className="text-brand-text font-bold text-4xl">
                      {draft.firstName?.charAt(0) || "?"}
                    </span>
                  </div>
              }
            </div>
            {isEditing && (
              <>
                <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
                <button onClick={() => fileInputRef.current?.click()}
                  className="absolute -bottom-1 -right-1 w-8 h-8 bg-brand rounded-full flex items-center justify-center shadow-elevated active:scale-90 transition-transform border-2 border-surface">
                  <i className="ri-camera-line text-on-brand text-xs" />
                </button>
              </>
            )}
          </div>
          <p className="text-primary font-bold text-base mt-3 font-display">
            {`${draft.firstName} ${draft.lastName}`.trim() || "Your Name"}
          </p>
          {user?.kycStatus === "approved" && (
            <div className="flex items-center gap-1 mt-1 px-2.5 py-0.5 rounded-full bg-success-light border border-success-border">
              <i className="ri-shield-check-line text-success text-xs" />
              <span className="text-success-text text-xs font-semibold">KYC Verified</span>
            </div>
          )}
        </div>

        {/* Fields */}
        <div className="bg-surface border border-border rounded-2xl overflow-hidden divide-y divide-border">
          {fields.map(field => (
            <div key={field.key} id={`field-${field.key}`}
              className="flex items-start gap-3 px-4 py-4">
              <div className="w-8 h-8 rounded-lg bg-surface-muted border border-border flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className={`${field.icon} text-muted text-sm`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-muted text-[11px] font-semibold uppercase tracking-wide mb-1">
                  {field.label}
                </p>
                {isEditing && !field.locked ? (
                  field.type === "select" ? (
                    <select
                      id={`input-${field.key}`}
                      value={draft[field.key] || ""}
                      onChange={e => setDraft(prev => ({ ...prev, [field.key]: e.target.value }))}
                      className="w-full text-secondary text-sm font-medium bg-transparent border-b border-brand-border pb-1 outline-none">
                      {field.options.map(o => (
                        <option key={o} value={o}>
                          {o === "" ? "Select…" : o.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={`input-${field.key}`}
                      type={field.type}
                      value={draft[field.key] || ""}
                      onChange={e => setDraft(prev => ({ ...prev, [field.key]: e.target.value }))}
                      className="w-full text-secondary text-sm font-medium bg-transparent border-b border-brand-border pb-1 outline-none"
                    />
                  )
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-secondary text-sm font-medium">
                      {draft[field.key] || <span className="text-faint italic">Not set</span>}
                    </p>
                    {field.locked && (
                      <span className="text-faint text-[10px] bg-surface-hover px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                        Locked
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <p className="text-muted text-xs text-center px-4">
          Email and country are locked for security. Contact support to change them.
        </p>
      </div>
    </div>
  );
}
